import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Input,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
} from "@salestrak/ui";

// Rename + delete dialogs for custom roles, launched from the Custom
// Role Permissions matrix's per-column menu. Presentation only — the
// caller owns the mutations (useCustomRoleActions) and passes async
// confirm handlers.

export function RenameRoleDialog({
  open,
  onOpenChange,
  currentName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  onConfirm: (nextName: string) => Promise<void>;
}) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);

  // Re-seed the input each time the dialog opens for a (possibly
  // different) role.
  useEffect(() => {
    if (open) {
      setName(currentName);
      setSaving(false);
    }
  }, [open, currentName]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onConfirm(trimmed);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to rename role:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Rename role</DialogTitle>
          <DialogDescription>
            Update the display name for this role. Existing permissions and
            assignments stay intact.
          </DialogDescription>
        </DialogHeader>
        {/* A real form element: Enter submits, and focus selects the
            current name. */}
        <Box
          as="form"
          paddingX="lg"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
          />
        </Box>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            loading={saving}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

// Sentinel for the "don't reassign" option — Select needs a non-empty
// value, and this maps back to `undefined` (drop the assignment) on confirm.
const NO_REASSIGN = "__none__";

export function DeleteRoleDialog({
  open,
  onOpenChange,
  roleName,
  reassignRoles,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  // Candidate roles holders can be moved onto (everything except the role
  // being deleted). Empty → the picker is hidden and delete just drops the
  // assignment.
  reassignRoles: ReadonlyArray<{ id: string; roleName: string }>;
  // `reassignToRoleId` is undefined when the admin keeps "Don't reassign".
  onConfirm: (reassignToRoleId?: string) => Promise<void>;
}) {
  const [deleting, setDeleting] = useState(false);
  const [reassignTo, setReassignTo] = useState<string>(NO_REASSIGN);

  useEffect(() => {
    if (open) {
      setDeleting(false);
      setReassignTo(NO_REASSIGN);
    }
  }, [open]);

  const selectedLabel =
    reassignTo === NO_REASSIGN
      ? "Don't reassign"
      : (reassignRoles.find((r) => r.id === reassignTo)?.roleName ??
        "Don't reassign");

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await onConfirm(reassignTo === NO_REASSIGN ? undefined : reassignTo);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to delete role:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{roleName}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            Members currently holding this role keep their other roles. This
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {reassignRoles.length > 0 ? (
          <Box gap="2xs" paddingX="lg" paddingTop="sm">
            <Text size="sm" weight="semibold">
              Reassign members to
            </Text>
            <Text size="xs" variant="muted">
              Optionally move everyone holding this role onto another role.
            </Text>
            {/* Base UI's Select is value-based (no `{value,label}` Option). */}
            <Select
              value={reassignTo}
              onValueChange={(next: string | null) => {
                if (next) setReassignTo(next);
              }}
            >
              <SelectTrigger size="sm">
                <SelectValue>{() => selectedLabel}</SelectValue>
              </SelectTrigger>
              <SelectPopup>
                <SelectItem value={NO_REASSIGN}>Don&rsquo;t reassign</SelectItem>
                {reassignRoles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.roleName}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </Box>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogClose
            render={<Button variant="outline" />}
            disabled={deleting}
          >
            Cancel
          </AlertDialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
